/**
 * Dashboard layout helpers. The pure, surface-agnostic mutations live in
 * `~/components/customize/layoutHelpers`; this module re-exports them and adds
 * the dashboard-specific normalization config (full-bleed widgets and the
 * legacy `site-controls` widget that is now page chrome).
 */
import {
  getReadModeRow as getReadModeRowGeneric,
  normalizeFullBleedRows as normalizeFullBleedRowsGeneric,
  stripPinnedWidgets
} from '~/components/customize/layoutHelpers'
import type { DashboardLayout, Row } from '~/components/customize/types'

export {
  applyAddRow,
  applyChangeRowLayout,
  applyMoveOrSwap,
  applyRemoveRow,
  applyRemoveWidget,
  applySetWidget,
  applySetWidgetSettings,
  findSlotContext,
  getPlacedWidgetIds,
  isSwapAllowed,
  stripPinnedWidgets
} from '~/components/customize/layoutHelpers'
export type { SlotContext } from '~/components/customize/layoutHelpers'

/** Widget ids that must occupy a full-width row to span the dashboard. */
const FULL_BLEED_WIDGET_IDS: ReadonlySet<string> = new Set(['charger-schedule', 'connector-overview'])

export function isFullBleedWidgetId(widgetId: string): boolean {
  return FULL_BLEED_WIDGET_IDS.has(widgetId)
}

/** Read-mode row shape: full-bleed lone widgets always render as a full-width row. */
export function getReadModeRow(row: Row): Row {
  return getReadModeRowGeneric(row, isFullBleedWidgetId)
}

export function normalizeFullBleedRows(layout: DashboardLayout): DashboardLayout {
  return normalizeFullBleedRowsGeneric(layout, isFullBleedWidgetId)
}

/** Legacy widget promoted to page chrome — stripped from persisted layouts. */
const LEGACY_PINNED_WIDGET_IDS: ReadonlySet<string> = new Set(['site-controls'])

/**
 * Canonical layout shape for render and mutation: strips legacy pinned widgets
 * (currently `site-controls`) and coerces lone full-bleed widgets into a single
 * full-width slot. Row-layout preview and Redux updates must run on this shape
 * so they match what the customize grid displays.
 */
export function normalizeDashboardLayout(layout: DashboardLayout): DashboardLayout {
  return normalizeFullBleedRows(stripPinnedWidgets(layout, LEGACY_PINNED_WIDGET_IDS))
}
